import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";
import { getSessionIdSafe } from "../../store/sagas/selectors";
import { configuration } from "../../api/client/SdsWebApiClient";
import { useRemoteControlEnabled } from "../../hooks/useRemoteControlEnabled";

const QrCodeContainer = styled.div<{ $embedded?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: white;
    padding: ${({ $embedded }) => ($embedded ? "6px" : "8px")};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    ${({ $embedded }) =>
        !$embedded &&
        `
        position: absolute;
        top: 20px;
        left: 20px;
        z-index: 1000;
    `}
`;

const QrCodeImage = styled.img`
    width: 120px;
    height: 120px;
    display: block;
`;

const QrCodePlaceholder = styled.div`
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    color: #999;
    font-size: 12px;
    text-align: center;
    padding: 10px;
`;

const TestQrCodesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
`;

const TestQrCodeItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const TestQrCodeLabel = styled.div`
    font-size: 11px;
    color: #666;
    font-weight: 500;
    text-align: center;
`;

const TestQrCodeImage = styled.img`
    width: 100px;
    height: 100px;
    display: block;
`;

interface TestQrCode {
    label: string;
    retailerId: string;
}

const TEST_QR_CODES: TestQrCode[] = [
    { label: "Tile", retailerId: "SBASSORTP006427" },
    { label: "Wallpaper", retailerId: "BR4511" },
    { label: "Cabinet", retailerId: "__ws-03" },
    { label: "Color for cabinet", retailerId: "__ws-03-bl" },
];

interface RemoteControlQrCodeProps {
    embedded?: boolean;
}

export const RemoteControlQrCode: React.FC<RemoteControlQrCodeProps> = ({ embedded }) => {
    const sessionId = useSelector(getSessionIdSafe);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const showRemoteControlLink = useRemoteControlEnabled();

    const urlParams = new URLSearchParams(window.location.search);
    const ShowRemoteTests = urlParams.get("show_remote_tests")?.toLowerCase() === "true";

    const basePath = configuration.basePath || window.location.origin;

    useEffect(() => {
        if (!showRemoteControlLink || !sessionId) {
            setQrCodeUrl(null);
            return;
        }

        // Build the QR code image URL
        const qrCodeImageUrl = `${basePath}/remote_control/qr?session_id=${encodeURIComponent(sessionId)}`;

        // Set the URL - the browser will handle loading the image
        setQrCodeUrl(qrCodeImageUrl);
        setError(null);
    }, [showRemoteControlLink, sessionId, basePath]);

    if (!showRemoteControlLink) {
        return null;
    }

    return (
        <QrCodeContainer $embedded={embedded}>
            {error ? (
                <QrCodePlaceholder>Error loading QR code</QrCodePlaceholder>
            ) : !qrCodeUrl ? (
                <QrCodePlaceholder>Loading...</QrCodePlaceholder>
            ) : (
                <QrCodeImage
                    src={qrCodeUrl}
                    alt="Remote Control QR Code"
                    onError={() => setError("Failed to load QR code")}
                />
            )}

            {ShowRemoteTests && (
                <TestQrCodesContainer>
                    {TEST_QR_CODES.map(testQrCode => (
                        <TestQrCodeItem key={testQrCode.retailerId}>
                            <TestQrCodeLabel>{testQrCode.label}</TestQrCodeLabel>
                            <TestQrCodeImage
                                src={`${basePath}/generate_product_qr?retailer_id=${encodeURIComponent(
                                    testQrCode.retailerId
                                )}`}
                                alt={`Test QR Code: ${testQrCode.label}`}
                            />
                        </TestQrCodeItem>
                    ))}
                </TestQrCodesContainer>
            )}
        </QrCodeContainer>
    );
};
